import React, {useEffect, useMemo, useState} from 'react';
import {SelectInput, DateInput, Button} from '@/ui-kits';
import {ConsultationGroup, ConsultationStatus, ConsultationType, SortOption} from '@/constants/enums';
import {cn, enumToSelectOptions} from '@/utils/common_functions';

interface FilterType {
  type: ConsultationType | null;
  status: ConsultationStatus | null;
  sort: SortOption | null;
  deadline: Date | null;
}

interface ConsultationFiltersProps extends FilterType {
  activeTab: ConsultationGroup;
  onReset?: () => void;
  onApply?: (args: FilterType) => void;
  screen_type: 'specialist' | 'patient';
}

export const ConsultationFilters: React.FC<ConsultationFiltersProps> = (props) => {
  const {type, status, sort, deadline, onReset, onApply, activeTab, screen_type = 'specialist'} = props;
  
  const [filters, setFilters] = useState<FilterType>({
    type: null,
    status: null,
    sort: null,
    deadline: null,
  });

  // Tracking if filters have been applied different from props
  const [appliedFilters, setAppliedFilters] = useState<FilterType>({
    type: null,
    status: null,
    sort: null,
    deadline: null,
  });

  // Check current filters options differ from applied filters
  const hasUnappliedChanges = useMemo(() => {
    return (
      filters.type !== appliedFilters.type ||
      filters.status !== appliedFilters.status ||
      filters.sort !== appliedFilters.sort ||
      filters.deadline !== appliedFilters.deadline
    );
  }, [filters, appliedFilters]);

  // Check if any filters are currently applied
  const hasAppliedFilters = useMemo(() => {
    return appliedFilters.type || appliedFilters.status || appliedFilters.sort || appliedFilters.deadline;
  }, [appliedFilters]);

  const typeOptions = enumToSelectOptions(ConsultationType);

  const statusOptions = useMemo(() => {
    if (activeTab === ConsultationGroup.Ongoing) {
      return [
        {id: ConsultationStatus.AwaitingDoctorReview, label: 'Awaiting Review'},
        {id: ConsultationStatus.UnderReviewByDoctor, label: 'Under Review'},
        {id: ConsultationStatus.Overdue, label: 'Overdue'},
      ];
    }
    if (activeTab === ConsultationGroup.ConsultationHistory) {
      return [
        {id: ConsultationStatus.Completed, label: 'Completed'},
        {id: ConsultationStatus.CancelledByPatient, label: 'Cancelled'},
      ];
    }
    return [
      {id: ConsultationStatus.AwaitingDoctorReview, label: 'Awaiting Review'},
      {id: ConsultationStatus.UnderReviewByDoctor, label: 'Under Review'},
      {id: ConsultationStatus.Overdue, label: 'Overdue'},
    ];
  }, [activeTab]);

  const sortPlaceholder = useMemo(() => {
    if (screen_type === 'specialist')
      return (activeTab === ConsultationGroup.ConsultationHistory ? "Sort by Reviewed / Cancelled Date" : "Sort by Reviewed Date");
    return "Sort";
  }, [activeTab, screen_type]);

  const sortOptions = [
    {id: SortOption.DESC, label: 'Newest -> Oldest'},
    {id: SortOption.ASC, label: 'Oldest -> Newest'},
  ];

  const handleApply = () => {
    setAppliedFilters(filters);
    onApply?.(filters);
  };

  const handleReset = () => {
    const resetFilters = {
      type: null,
      status: null,
      sort: null,
      deadline: null,
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    onReset?.();
  };

  useEffect(() => {
    setFilters({
      type: type || null,
      status: status || null,
      sort: sort || null,
      deadline: deadline || null,
    });
    setAppliedFilters({
      type: type || null,
      status: status || null,
      sort: sort || null,
      deadline: deadline || null,
    });
  }, [type, status, sort, deadline]);

  return (
    <div className="bg-gray-50 px-4 sm:px-6 py-3 overflow-x-hidden">
      <div className="flex flex-col lg:flex-row lg:flex-wrap items-stretch lg:items-center lg:justify-center gap-3 min-w-0">
        {/* Type and Status filters in one row on mobile */}
        <div className="flex flex-row lg:contents gap-2 w-full">
          <div className="flex-1 lg:w-[205px] lg:flex-none max-w-full">
            <SelectInput
              label=""
              placeholder="Filter by Type"
              value={filters.type}
              onChange={item => setFilters({...filters, type: item.id})}
              options={typeOptions}
              wrapperClassName="h-[44px] border rounded-lg bg-white [&_img]:!opacity-100 !w-full"
              textClassName="text-sm"
              usePortal
              className="[&_img]:![filter:brightness(0)_saturate(100%)_invert(21%)_sepia(18%)_saturate(1047%)_hue-rotate(159deg)_brightness(94%)_contrast(93%)]"
            />
          </div>

          {activeTab !== ConsultationGroup.Past && (
            <div className="flex-1 lg:w-[205px] lg:flex-none max-w-full">
              <SelectInput
                label=""
                placeholder="Filter by Status"
                value={filters.status}
                onChange={item => setFilters({...filters, status: item.id})}
                options={statusOptions}
                dropDownClassname='w-full'
                usePortal
                wrapperClassName="h-[44px] border rounded-lg bg-white [&_img]:!opacity-100 !w-full"
                className="[&_img]:![filter:brightness(0)_saturate(100%)_invert(21%)_sepia(18%)_saturate(1047%)_hue-rotate(159deg)_brightness(94%)_contrast(93%)]"
              />
            </div>
          )}
        </div>

        {activeTab === ConsultationGroup.Ongoing && (
          <div className="w-full lg:w-[242px] max-w-full relative">
            <SelectInput
              label=""
              leftIcon="sort"
              iconClassName="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 z-10 pointer-events-none [filter:brightness(0)_saturate(100%)_invert(21%)_sepia(18%)_saturate(1047%)_hue-rotate(159deg)_brightness(94%)_contrast(93%)]"
              placeholder="Sort by Booking Date"
              value={filters.sort}
              onChange={item => setFilters({...filters, sort: item.id})}
              options={sortOptions}
              wrapperClassName="h-[44px] border rounded-lg bg-white [&_img]:!opacity-100 [&>p]:pl-5 !w-full"
              textClassName="text-sm truncate"
              usePortal
              className="[&_img]:![filter:brightness(0)_saturate(100%)_invert(21%)_sepia(18%)_saturate(1047%)_hue-rotate(159deg)_brightness(94%)_contrast(93%)]"
            />
          </div>
        )}

        {activeTab === ConsultationGroup.Ongoing && (
          <div className="w-full lg:w-[242px] max-w-full relative">
            <DateInput
              label=""
              placeholder="Deadline"
              value={filters.deadline}
              onChangeDate={date => setFilters({...filters, deadline: date})}
              wrapperClassName="py-0 border rounded-lg bg-white [&_img]:!opacity-100 !w-full"
              textClassName="!py-2 text-ui_input_border_dark text-sm"
              iconClassName="![filter:brightness(0)_saturate(100%)_invert(21%)_sepia(18%)_saturate(1047%)_hue-rotate(159deg)_brightness(94%)_contrast(93%)]"
            />
          </div>
        )}

        {activeTab !== ConsultationGroup.Ongoing && (
          <div className={cn(
            "flex flex-row lg:contents gap-2 w-full",
            screen_type === 'patient' && 'lg:min-w-[220px]'
          )}>
            <div className={cn(
              "flex-1 lg:min-w-[330px] lg:w-auto lg:flex-none max-w-full",
              screen_type === 'patient' && 'lg:min-w-[220px]'
            )}>
              <SelectInput
                label=""
                leftIcon="sort"
                iconClassName="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 z-10 pointer-events-none [filter:brightness(0)_saturate(100%)_invert(21%)_sepia(18%)_saturate(1047%)_hue-rotate(159deg)_brightness(94%)_contrast(93%)]"
                placeholder={sortPlaceholder}
                value={filters.sort}
                onChange={item => setFilters({...filters, sort: item.id})}
                options={sortOptions}
                usePortal
                wrapperClassName="h-[44px] border rounded-lg bg-white [&_img]:!opacity-100 [&>p]:pl-5 !w-full"
                textClassName="mr-2 text-sm truncate"
                dropDownClassname="w-full"
              />
            </div>
            {(hasUnappliedChanges || hasAppliedFilters) && (
              <>
                {hasAppliedFilters && (
                  <div className="flex-1 lg:w-auto lg:flex-none max-w-full">
                    <Button 
                      title="Reset Filters" 
                      variant="secondary" 
                      onClick={handleReset} 
                      className="h-[44px] px-6 text-sm font-FigtreeMedium !w-full lg:!w-auto" 
                    />
                  </div>
                )}
                {/* Apply button shows when there are unapplied changes */}
                {hasUnappliedChanges && (
                  <div className="flex-1 lg:w-auto lg:flex-none max-w-full">
                    <Button
                      title="Apply Filters"
                      variant="primary"
                      onClick={handleApply}
                      className="h-[44px] px-6 text-sm font-FigtreeMedium !w-full lg:!w-auto"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === ConsultationGroup.Ongoing && (hasUnappliedChanges || hasAppliedFilters) && (
          <div className="flex flex-row lg:contents gap-2 w-full">
            {hasAppliedFilters && (
              <div className="flex-1 lg:w-auto lg:flex-none max-w-full">
                <Button 
                  title="Reset Filters" 
                  variant="secondary" 
                  onClick={handleReset} 
                  className="h-[44px] px-6 text-sm font-FigtreeMedium !w-full lg:!w-auto" 
                />
              </div>
            )}
            {/* Apply button shows when there are unapplied changes */}
            {hasUnappliedChanges && (
              <div className="flex-1 lg:w-auto lg:flex-none max-w-full">
                <Button
                  title="Apply Filters"
                  variant="primary"
                  onClick={handleApply}
                  className="h-[44px] px-6 text-sm font-FigtreeMedium !w-full lg:!w-auto"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};